import { ReactNode } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import { Registry } from '@/react/portainer/registries/types/registry';

import { FormSection } from '@@/form-components/FormSection';
import { MultiRegistrySelectFieldset } from '@@/form-components/MultiRegistrySelectFieldset';

type Value = Array<Registry['Id']>;

export function RegistriesField({
  onChange,
  value,
  errors,
}: {
  value: Value;
  onChange(value: Value): void;
  errors?: ReactNode;
}) {
  const envId = useEnvironmentId();
  const registriesQuery = useEnvironmentRegistries(envId, {
    enabled: !!envId,
    hideDefault: true,
  });

  if (registriesQuery.isLoading || !registriesQuery.data) {
    return (
      <FormSection title="Registries">
        <span className="small text-muted">Loading registries...</span>
      </FormSection>
    );
  }

  if (registriesQuery.data.length === 0) {
    return (
      <FormSection title="Registries">
        <span className="small text-muted">
          No registries available. Please configure registries first.
        </span>
      </FormSection>
    );
  }

  const selectedRegistries = value
    .map((id) => registriesQuery.data.find((r) => r.Id === id))
    .filter((i): i is Registry => !!i);

  return (
    <FormSection title="Registries">
      <MultiRegistrySelectFieldset
        options={registriesQuery.data}
        value={selectedRegistries}
        onChange={(registries) => onChange(registries.map((r) => r.Id))}
        placeholder="Choose one or more registries for this stack"
        emptyText="No registries available. Please configure registries first."
        tooltip="Defaults to all registries. Select specific registry when using multiple registries from same provider to avoid Docker credential conflicts."
        errors={errors}
      />
    </FormSection>
  );
}
